/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {ReactNode, useState} from "react";
import {
    ChatContainer,
    ChatFooter,
    ChatInput,
    ChatScroll,
    ChatThemeProvider,
    getMessageSequencePosition,
} from "@unibuddy/chat-ui";
import {Box} from "@unibuddy/patron";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";

import {ChatMessage, UniversityField} from "src/types/generated";
import {MentorAvatar} from "shared/Mentor/components/MentorAvatar/MentorAvatar";
import shouldShowPlaceholderAvatar from "shared/Chat/functions/shouldShowPlaceholderAvatar";
import {DEANONYMISE_PROSPECT_ENABLED} from "shared/constants";

import useUniversityColor from "../../../UniversityProvider/hooks/useUniversityColor";
import {SecureMessage} from "../SecureMessage/SecureMessage";
import {widgetChatTheme} from "../../theme/theme";
import {ChatInterventionBanner} from "../ChatInterventionBanner/ChatInterventionBanner";
import {EmptyAvatar} from "../ChatAvatar/EmptyAvatar";
import {UserRequiredId} from "../../types";
import {SensitiveDataWarningPopUp} from "../ChatWarningMessagePopUp/SensitiveDataWarningPopUp";
import {Message} from "../Message/Message";
import {ChatHeader} from "../ChatHeader/ChatHeader";

type SendMessageProps = {
    text: string;
};

type ChatUIProps = {
    messages: ChatMessage[];
    user: UserRequiredId;
    chatPopup?: ReactNode;
    queue?: ReactNode;
    university?: UniversityField;
    chatFooterHidden?: boolean;
    chatInputDisabled?: boolean;
    onSend?: (payload: SendMessageProps) => Promise<void>;
};

export function ChatUI({
    messages,
    user,
    chatPopup,
    university,
    chatFooterHidden,
    chatInputDisabled,
    queue,
    onSend,
}: ChatUIProps) {
    const [chatInput, setChatInput] = useState("");
    const [isSensitiveWarningPopUpVisible, setIsSensitiveWarningPopUpVisible] = useState(false);
    const color = useUniversityColor();
    const isDeanonymiseProspectEnabled = useBooleanFeatureFlag(
        DEANONYMISE_PROSPECT_ENABLED.key,
        DEANONYMISE_PROSPECT_ENABLED.value,
    );

    const chatInterventionMessages = messages.filter(
        (m) => m.sender && m.sender.accountRole === "university",
    );

    return (
        //@ts-ignore
        <ChatThemeProvider theme={widgetChatTheme(color)}>
            <ChatContainer
                borderWidth={1}
                borderColor="#e4e8eb"
                borderRadius="xsmall"
                overflow="hidden"
            >
                <ChatHeader
                    chatRulesOverride={messages.length > 1}
                    user={user}
                />
                {chatInterventionMessages.length > 0 ? <ChatInterventionBanner /> : null}
                <ChatScroll
                    messages={messages}
                    blur={false}
                    loading={false}
                    hasMoreMessages={false}
                >
                    {isDeanonymiseProspectEnabled ? (
                        <Box paddingTop={["xxsmall", "xsmall", "small"]} role="listitem" />
                    ) : (
                        <SecureMessage />
                    )}
                    {messages.map((message: ChatMessage, index: number, arr: ChatMessage[]) => {
                        const position = getMessageSequencePosition({
                            //@ts-ignore
                            currentMessage: messages[index],
                            //@ts-ignore
                            nextMessage: messages[index + 1],
                            //@ts-ignore
                            previousMessage: messages[index - 1],
                        });
                        const isMyMessage = message.sender.id === user?.id;
                        return (
                            <>
                                {message.text !== "" && (
                                    <Message
                                        key={message.id}
                                        position={position}
                                        align={isMyMessage ? "right" : "left"}
                                        datestamp={message.created}
                                        university={university}
                                        isMyMessage={isMyMessage}
                                        avatar={
                                            // eslint-disable-next-line no-nested-ternary
                                            isMyMessage ? null : (
                                                <Box
                                                    paddingRight={["xxsmall", "xsmall"]}
                                                    flexShrink={0}
                                                >
                                                    {shouldShowPlaceholderAvatar(index, arr) ? (
                                                        <EmptyAvatar />
                                                    ) : (
                                                        <MentorAvatar mentor={message.sender} />
                                                    )}
                                                </Box>
                                            )
                                        }
                                    >
                                        {message.text}
                                    </Message>
                                )}
                            </>
                        );
                    })}
                </ChatScroll>
                {queue}
                {chatPopup || null}
                {isSensitiveWarningPopUpVisible && (
                    <SensitiveDataWarningPopUp
                        onCloseClick={() => setIsSensitiveWarningPopUpVisible(false)}
                    />
                )}
                {!chatFooterHidden && (
                    <ChatFooter>
                        <ChatInput
                            onSend={(text) => {
                                (async () => {
                                    await onSend?.({text});
                                })();
                            }}
                            value={chatInput}
                            onChangeText={setChatInput}
                            disabled={chatInputDisabled}
                        />
                    </ChatFooter>
                )}
            </ChatContainer>
        </ChatThemeProvider>
    );
}
